import * as React from "react";
import Header from "@amzn/awsui-components-react/polaris/header";
import Container from "@amzn/awsui-components-react/polaris/container";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import Button from "@amzn/awsui-components-react/polaris/button";
import { API } from "aws-amplify";
import { MedicationOption } from "../add_prescription/PrescriptionDetailsBlock";
import { NewRxItemEntry, EMPTY_ITEM } from "./NewRxItemTypes";
import NewRxItemTabContent from "./NewRxItemTabContent";
import { validateNewRxBatch, buildNewRxBatchRequest } from "./NewRxRequestBuilder";
import { determineIdentitySource, PharmacyIdentitySource } from "../../../../../../consts/Patterns";
import type {
  IngestNewRxBatchResponse,
  NewRxBatchStartResult,
} from "@amzn/apaqportaljavalambdatypescriptclient";

type StartResult = NewRxBatchStartResult & { label: string };

interface State {
  identityValue: string;
  identityTouched: boolean;
  items: NewRxItemEntry[];
  activeTabIndex: number;
  medications: MedicationOption[];
  loadingMeds: boolean;
  submitting: boolean;
  results: StartResult[] | null;
  executionName: string | null;
  error: string | null;
}

/**
 * Add Prescription via NewRx — batch NewRx ingestion (IngestNewRxBatch).
 *
 * Parent orchestrator (mirrors AddPrescriptionV2): owns patient identity, the per-item tabs,
 * submit/validation, the API call, and results. This scaffold implements the User Details block
 * (personId OR profileId), medication loading, and the multi-item tab bar; per-item content,
 * submit and results are added in later tasks.
 */
class AddPrescriptionNewRx extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      identityValue: "",
      identityTouched: false,
      items: [{ ...EMPTY_ITEM }],
      activeTabIndex: 0,
      medications: [],
      loadingMeds: true,
      submitting: false,
      results: null,
      executionName: null,
      error: null,
    };
  }

  componentDidMount() {
    this.loadMedications();
  }

  private loadMedications = async () => {
    try {
      const result = await API.get("ApaqPortalAPI", "/drug/get-drugs", {});
      const meds: MedicationOption[] = (result.medications || []).map((m: any) => ({
        drugId: m.drugId,
        description: m.description || m.drugId,
      }));
      this.setState({ medications: meds, loadingMeds: false });
    } catch (e) {
      this.setState({ error: "Failed to load medications", loadingMeds: false });
    }
  };

  /** Auto-detected identity source (personId vs profileId) by shape, or null when unrecognized. */
  private get detectedIdentitySource(): PharmacyIdentitySource | null {
    return determineIdentitySource(this.state.identityValue);
  }

  /** True when the identity value is a recognized Person ID or Profile ID. Gates submit. */
  private get isIdentityValid(): boolean {
    return this.detectedIdentitySource !== null;
  }

  private addTab = () => {
    this.setState((prev) => ({
      items: [...prev.items, { ...EMPTY_ITEM }],
      activeTabIndex: prev.items.length,
    }));
  };

  private removeTab = (index: number) => {
    this.setState((prev) => {
      if (prev.items.length <= 1) return null;
      const items = prev.items.filter((_, i) => i !== index);
      const activeTabIndex =
        prev.activeTabIndex >= items.length
          ? items.length - 1
          : prev.activeTabIndex > index
            ? prev.activeTabIndex - 1
            : prev.activeTabIndex;
      return { items, activeTabIndex };
    });
  };

  private updateEntry = (updated: NewRxItemEntry) => {
    this.setState((prev) => {
      const items = [...prev.items];
      items[prev.activeTabIndex] = updated;
      return { items };
    });
  };

  /** Human label for an item's result row (drug name/id or a positional fallback). */
  private itemLabel = (index: number | undefined): string => {
    if (index === undefined) return "Prescription";
    const entry = this.state.items[index];
    if (!entry) return `Prescription ${index + 1}`;
    return (
      entry.drugIdInput.replace(/\s*\(.*\)$/, "") ||
      entry.drugId ||
      entry.medication.ndc ||
      `Prescription ${index + 1}`
    );
  };

  private handleSubmit = async () => {
    const { identityValue, items } = this.state;
    const identitySource = this.detectedIdentitySource;

    if (!identitySource) {
      this.setState({
        error:
          "Enter a valid Person ID (amzn1.actor.person.oid.…) or Profile ID (amzn1.pharmacy.profile.id.…)",
        identityTouched: true,
        results: null,
        executionName: null,
      });
      return;
    }

    const validation = validateNewRxBatch(identityValue, identitySource, items);
    if (!validation.valid) {
      this.setState({
        error: validation.message || "Invalid request",
        activeTabIndex: validation.itemIndex ?? this.state.activeTabIndex,
        identityTouched: true,
        results: null,
        executionName: null,
      });
      return;
    }

    this.setState({ submitting: true, error: null, results: null, executionName: null });
    try {
      const request = buildNewRxBatchRequest(identityValue, identitySource, items);
      const response = (await API.post("ApaqPortalAPI", "/prescription-messaging/ingest-new-rx-batch", {
        body: request,
      })) as IngestNewRxBatchResponse;
      const results: StartResult[] = (response.results || []).map((r) => ({
        ...r,
        label: this.itemLabel(r.index),
      }));
      this.setState({
        results,
        executionName: response.executionName || null,
        submitting: false,
      });
    } catch (e) {
      const err = e as any;
      const message =
        err?.response?.data?.message || (err instanceof Error ? err.message : "Request failed");
      this.setState({ error: message, submitting: false });
    }
  };

  render() {
    const { identityValue, identityTouched, items, activeTabIndex, medications, loadingMeds, submitting, results, executionName, error } =
      this.state;
    const detectedSource = this.detectedIdentitySource;
    const identityError =
      identityTouched && !this.isIdentityValid
        ? "Enter a valid Person ID (amzn1.actor.person.oid.…) or Profile ID (amzn1.pharmacy.profile.id.…)"
        : undefined;
    const identityConstraint = detectedSource
      ? `Detected: ${detectedSource === "personId" ? "Person ID" : "Profile ID"}`
      : "Accepts a Person ID or a Profile ID";

    if (loadingMeds) return <Spinner size="large" />;

    return (
      <div>
        <Header variant="h2">Add Prescription via NewRx</Header>

        <SpaceBetween size="l">
          {error && (
            <Flashbar
              items={[
                {
                  type: "error",
                  content: error,
                  dismissible: true,
                  onDismiss: () => this.setState({ error: null }),
                },
              ]}
            />
          )}

          {results && (
            <Flashbar
              items={[
                ...(executionName
                  ? [
                      {
                        type: "info" as const,
                        content: `Batch execution started: ${executionName}`,
                      },
                    ]
                  : []),
                ...results.map((r) => ({
                  type: r.started ? ("success" as const) : ("error" as const),
                  content: `${r.label} (item ${r.index}${r.ndc ? `, NDC ${r.ndc}` : ""}): ${
                    r.started ? "Started" : r.errorMessage || "Not started"
                  }`,
                })),
              ]}
            />
          )}

          <Flashbar
            items={[
              {
                type: "info",
                content:
                  "Batch NewRx ingestion is asynchronous. Submitting starts a background workflow — " +
                  "a success response means the batch was started, not that prescriptions were created or approved.",
              },
            ]}
          />

          <Container header={<Header variant="h3">User Details</Header>}>
            <FormField
              label="Person ID or Profile ID"
              description="Required. The identifier type is detected automatically from its format."
              constraintText={identityConstraint}
              errorText={identityError}
            >
              <Input
                value={identityValue}
                onChange={({ detail }) => this.setState({ identityValue: detail.value })}
                onBlur={() => this.setState({ identityTouched: true })}
                placeholder="amzn1.actor.person.oid.… or amzn1.pharmacy.profile.id.…"
              />
            </FormField>
          </Container>

          <Tabs
            activeTabId={String(activeTabIndex)}
            onChange={({ detail }) =>
              this.setState({ activeTabIndex: Number(detail.activeTabId) })
            }
            tabs={items.map((item, i) => ({
              id: String(i),
              label: (item.drugIdInput || `Prescription ${i + 1}`).replace(/\s*\(.*\)$/, ""),
              dismissible: items.length > 1,
              dismissLabel: `Remove Prescription ${i + 1}`,
              onDismiss: () => this.removeTab(i),
            }))}
            actions={<Button iconName="add-plus" variant="icon" onClick={this.addTab} />}
          />

          <NewRxItemTabContent
            entry={items[activeTabIndex]}
            medications={medications}
            onChange={this.updateEntry}
          />

          <Button
            variant="primary"
            loading={submitting}
            disabled={!this.isIdentityValid}
            onClick={this.handleSubmit}
          >
            Submit
          </Button>
        </SpaceBetween>
      </div>
    );
  }
}

export default AddPrescriptionNewRx;
